import React from 'react';
import { X } from 'lucide-react';

/**
 * IQI Breakdown Modal
 * Shows detailed breakdown of Interaction Quality Index scores
 */
const IQIBreakdownModal = ({ iqi_scores, onClose, contentType = 'content' }) => {
  if (!iqi_scores) {
    return null;
  }

  const {
    clarity,
    depth,
    empathy,
    reciprocity,
    transformative,
    iqi_total,
    interpretation,
    clarity_reasoning,
    depth_reasoning,
    empathy_reasoning,
    reciprocity_reasoning,
    transformative_reasoning,
    strengths = [],
    improvements = []
  } = iqi_scores;

  const dimensions = [
    {
      name: 'Clarity',
      score: clarity,
      reasoning: clarity_reasoning,
      color: 'from-blue-500 to-blue-600',
      description: 'How clear, coherent, and understandable is the content?'
    },
    {
      name: 'Depth',
      score: depth,
      reasoning: depth_reasoning,
      color: 'from-purple-500 to-purple-600',
      description: 'How thorough, nuanced, and substantive is the thinking?'
    },
    {
      name: 'Empathy/Engagement',
      score: empathy,
      reasoning: empathy_reasoning,
      color: 'from-pink-500 to-rose-600',
      description: 'How well does it connect with others or invite engagement?'
    },
    {
      name: 'Reciprocity',
      score: reciprocity,
      reasoning: reciprocity_reasoning,
      color: 'from-emerald-500 to-teal-600',
      description: 'How well does it build on ideas or foster dialogue?'
    },
    {
      name: 'Transformative Potential',
      score: transformative,
      reasoning: transformative_reasoning,
      color: 'from-orange-500 to-amber-600',
      description: 'How likely is this to spark new thinking or drive action?'
    }
  ];

  return (
    <div className="fixed inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl max-w-4xl w-full max-h-[90vh] overflow-y-auto shadow-2xl">
        {/* Header */}
        <div className="sticky top-0 bg-gradient-to-r from-purple-600 to-pink-600 text-white p-6 rounded-t-2xl flex justify-between items-center z-10">
          <div>
            <h2 className="text-2xl font-bold flex items-center gap-3">
              <span className="text-4xl font-bold">{iqi_total.toFixed(1)}</span>
              <div>
                <div className="text-sm opacity-90">Interaction Quality Index</div>
                <div className="text-xl font-semibold">{interpretation}</div>
              </div>
            </h2>
            <p className="text-sm opacity-90 mt-2">AI-Powered Quality Analysis</p>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-white/20 rounded-full transition-colors"
          >
            <X className="w-6 h-6" />
          </button>
        </div>

        {/* Content */}
        <div className="p-6">
          {/* IQI Formula */}
          <div className="mb-8 bg-gradient-to-r from-gray-50 to-gray-100 p-6 rounded-xl">
            <h3 className="text-lg font-bold mb-3 text-gray-800">IQI Calculation</h3>
            <div className="text-sm text-gray-700 mb-3">
              IQI = (Clarity + Depth + Empathy + Reciprocity + Transformative) ÷ 5
            </div>
            <div className="text-sm text-gray-600 font-mono bg-white p-3 rounded-lg">
              {iqi_total.toFixed(1)} = ({clarity} + {depth} + {empathy} + {reciprocity} + {transformative}) ÷ 5
            </div>
          </div>

          {/* Dimension Breakdown */}
          <h3 className="text-xl font-bold mb-4 text-gray-800">Dimension Breakdown</h3>
          <div className="space-y-4 mb-8">
            {dimensions.map((dimension, index) => (
              <div key={index} className="border border-gray-200 rounded-xl p-5 hover:shadow-md transition-shadow">
                <div className="flex justify-between items-start mb-3">
                  <div className="flex-1">
                    <h4 className="font-bold text-lg text-gray-800 mb-1">{dimension.name}</h4>
                    <p className="text-sm text-gray-600 italic">{dimension.description}</p>
                  </div>
                  <div className={`ml-4 flex items-center justify-center w-16 h-16 rounded-full bg-gradient-to-br ${dimension.color} text-white font-bold text-xl shadow-lg`}>
                    {dimension.score.toFixed(1)}
                  </div>
                </div>
                
                {/* Score Bar */}
                <div className="mb-3">
                  <div className="w-full bg-gray-200 rounded-full h-2">
                    <div
                      className={`h-2 rounded-full bg-gradient-to-r ${dimension.color}`}
                      style={{ width: `${(dimension.score / 10) * 100}%` }}
                    />
                  </div>
                </div>

                {/* Reasoning */}
                <p className="text-sm text-gray-700 leading-relaxed">{dimension.reasoning}</p>
              </div>
            ))}
          </div>

          {/* Strengths */}
          {strengths && strengths.length > 0 && (
            <div className="mb-6">
              <h3 className="text-xl font-bold mb-3 text-gray-800 flex items-center gap-2">
                <span className="text-2xl">✨</span>
                Strengths
              </h3>
              <div className="bg-gradient-to-r from-emerald-50 to-teal-50 p-5 rounded-xl">
                <ul className="space-y-2">
                  {strengths.map((strength, index) => (
                    <li key={index} className="flex items-start gap-3 text-sm text-gray-700">
                      <span className="text-emerald-600 font-bold mt-0.5">✓</span>
                      <span>{strength}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          )}

          {/* Improvements */}
          {improvements && improvements.length > 0 && (
            <div>
              <h3 className="text-xl font-bold mb-3 text-gray-800 flex items-center gap-2">
                <span className="text-2xl">💡</span>
                Suggestions for Improvement
              </h3>
              <div className="bg-gradient-to-r from-orange-50 to-amber-50 p-5 rounded-xl">
                <ul className="space-y-2">
                  {improvements.map((improvement, index) => (
                    <li key={index} className="flex items-start gap-3 text-sm text-gray-700">
                      <span className="text-orange-600 font-bold mt-0.5">→</span>
                      <span>{improvement}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          )}

          {/* Interpretation Guide */}
          <div className="mt-8 bg-gray-50 p-5 rounded-xl">
            <h4 className="font-bold text-sm text-gray-700 mb-3">IQI Interpretation Guide</h4>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-xs">
              <div className="flex items-center gap-2">
                <div className="w-3 h-3 rounded-full bg-gradient-to-r from-purple-500 to-pink-500"></div>
                <span className="text-gray-600"><strong>9-10:</strong> Exceptional</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-3 h-3 rounded-full bg-gradient-to-r from-blue-500 to-cyan-500"></div>
                <span className="text-gray-600"><strong>7-9:</strong> High Quality</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-3 h-3 rounded-full bg-gradient-to-r from-emerald-500 to-teal-500"></div>
                <span className="text-gray-600"><strong>5-7:</strong> Moderate</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-3 h-3 rounded-full bg-gradient-to-r from-orange-500 to-yellow-500"></div>
                <span className="text-gray-600"><strong>&lt;5:</strong> Needs Improvement</span>
              </div>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="bg-gray-50 p-4 rounded-b-2xl text-center">
          <button
            onClick={onClose}
            className="px-6 py-2 bg-gradient-to-r from-purple-600 to-pink-600 text-white rounded-lg font-semibold hover:opacity-90 transition-opacity"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
};

export default IQIBreakdownModal;
